/**
 * Resolve package sell price for a sales-order / invoice line item pick.
 * Matches exact product + package labels (legacy "Product Type" and
 * preferred "Product (Type)") — never space-token parsing.
 */

export interface PackageTypePrice {
  type: string;
  price?: string | number | null;
  isKegDepositItem?: boolean;
  itemName?: string;
}

export interface ProductWithPackages {
  id: number;
  name: string;
  packageTypes?: PackageTypePrice[];
}

export interface ResolvedLinePrice {
  price: string | null;
  hasKegDeposit: boolean;
  productId: number | null;
  productName: string | null;
  packageType: string | null;
}

function normalizePrice(value: string | number | null | undefined): string | null {
  if (value == null || value === '') return null;
  const n = parseFloat(String(value));
  if (!Number.isFinite(n) || n < 0) return null;
  return n.toFixed(2);
}

/** Preferred packaging SKU label used in inventory / invoice lines. */
export function preferredPackagedSku(
  productName: string,
  packageType: string,
  pkgItemName?: string | null
): string {
  const fromPkg = String(pkgItemName || '').trim();
  const product = String(productName || '').trim();
  const type = String(packageType || '').trim();
  const bareType = !!(fromPkg && type && fromPkg.toLowerCase() === type.toLowerCase());
  const looksLikeFullSku =
    !!fromPkg &&
    !bareType &&
    (!product ||
      fromPkg.toLowerCase().includes(product.toLowerCase()) ||
      /\([^)]+\)/.test(fromPkg));
  if (looksLikeFullSku) return fromPkg;
  if (!product || !type) return fromPkg || product || type;
  return `${product} (${type})`;
}

export function legacyPackagedSku(productName: string, packageType: string): string {
  const product = String(productName || '').trim();
  const type = String(packageType || '').trim();
  if (!product || !type) return '';
  return `${product} ${type}`;
}

/** Unique SKU option values for SO/invoice item pickers (preferred + legacy). */
export function listPackagedSkuOptions(products: ProductWithPackages[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const product of products || []) {
    const productName = String(product.name || '').trim();
    if (!productName) continue;
    for (const pkg of product.packageTypes || []) {
      const type = String(pkg.type || '').trim();
      if (!type) continue;
      for (const value of [
        preferredPackagedSku(productName, type, pkg.itemName),
        legacyPackagedSku(productName, type),
      ]) {
        if (!value || seen.has(value)) continue;
        seen.add(value);
        out.push(value);
      }
    }
  }
  return out;
}

export function resolveProductPackagePrice(
  itemName: string,
  products: ProductWithPackages[]
): ResolvedLinePrice {
  const name = String(itemName || '').trim();
  const empty: ResolvedLinePrice = {
    price: null,
    hasKegDeposit: false,
    productId: null,
    productName: null,
    packageType: null,
  };
  if (!name || !products?.length) return empty;

  let best: (ResolvedLinePrice & { score: number }) | null = null;
  for (const product of products) {
    const productName = String(product.name || '').trim();
    if (!productName) continue;
    for (const pkg of product.packageTypes || []) {
      const type = String(pkg.type || '').trim();
      if (!type) continue;
      const legacy = `${productName} ${type}`;
      const preferred = `${productName} (${type})`;
      const pkgItem = String(pkg.itemName || '').trim();
      if (name !== legacy && name !== preferred && name !== pkgItem) continue;
      const score = productName.length;
      if (!best || score > best.score) {
        best = {
          score,
          price: normalizePrice(pkg.price),
          hasKegDeposit: !!pkg.isKegDepositItem,
          productId: product.id,
          productName,
          packageType: type,
        };
      }
    }
  }
  if (!best) return empty;
  return {
    price: best.price,
    hasKegDeposit: best.hasKegDeposit,
    productId: best.productId,
    productName: best.productName,
    packageType: best.packageType,
  };
}

/** True when a case package resolved to a $0.00 default sell price (warn, don't block). */
export function isZeroCasePackagePrice(resolved: ResolvedLinePrice): boolean {
  if (resolved.price == null) return false;
  if (!/case/i.test(String(resolved.packageType || ''))) return false;
  return parseFloat(resolved.price) === 0;
}
